import React, { useState, useEffect } from 'react';
import { db, storage } from '../../firebase';
import { collection, getDocs, query, orderBy, deleteDoc, doc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import PropertyForm, { initialPropertyState } from './PropertyForm';
import { FaPlus, FaEdit, FaTrash, FaSearch } from 'react-icons/fa';

function PropertyManager() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingProperty, setEditingProperty] = useState(null);
  const [showPropertyForm, setShowPropertyForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const propertiesCollectionRef = collection(db, 'properties');

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const q = query(propertiesCollectionRef, orderBy('date_status', 'desc'));
      const data = await getDocs(q);
      const fetchedProperties = data.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
        created_at: doc.data().created_at ? doc.data().created_at.toDate() : null,
        date_status: doc.data().date_status ? doc.data().date_status.toDate() : null,
      }));
      setProperties(fetchedProperties);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching properties:", err);
      setError("Failed to load properties. Please try again.");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  const filteredProperties = properties.filter(property => {
    const query = searchQuery.toLowerCase();
    if (!query) return true;

    if (property.display_id && String(property.display_id).includes(query)) return true;
    if (property.id.toLowerCase().includes(query)) return true;
    if (property.title && property.title.toLowerCase().includes(query)) return true;
    if (property.project_name && property.project_name.toLowerCase().includes(query)) return true;
    if (property.location && property.location.toLowerCase().includes(query)) return true;
    if (property.owner_contact && property.owner_contact.toLowerCase().includes(query)) return true;
    if (property.owner_name && property.owner_name.toLowerCase().includes(query)) return true;

    return false;
  });

  const handleEdit = (property) => {
    setEditingProperty(property);
    setShowPropertyForm(true);
  };

  const handleDelete = async (propertyId, imageUrls) => {
    if (window.confirm("Are you sure you want to delete this property?")) {
      setLoading(true);
      setError(null);
      try {
        for (const url of imageUrls) {
          try {
            const decodedUrl = decodeURIComponent(url);
            const pathStartIndex = decodedUrl.indexOf('/o/property_images/');
            if (pathStartIndex !== -1) {
              const path = decodedUrl.substring(pathStartIndex + 3);
              const questionMarkIndex = path.indexOf('?');
              const finalPath = questionMarkIndex !== -1 ? path.substring(0, questionMarkIndex) : path;
              const imageRef = ref(storage, finalPath);
              await deleteObject(imageRef);
              console.log("Deleted image from Storage:", finalPath);
            } else {
              console.warn("Could not determine storage path from URL for deletion:", url);
            }
          } catch (err) {
            console.warn("Error deleting image from Storage (might not exist or permissions issue):", url, err);
          }
        }
        await deleteDoc(doc(db, 'properties', propertyId));
        alert('Property deleted successfully!');
        fetchProperties();
      } catch (err) {
        console.error("Error deleting property:", err);
        setError("Failed to delete property. Please try again.");
        setLoading(false);
      }
    }
  };

  const handleSaveSuccess = () => {
    setEditingProperty(null);
    setShowPropertyForm(false);
    fetchProperties();
  };

  const handleCancelEdit = () => {
    setEditingProperty(null);
    setShowPropertyForm(false);
  };

  return (
    <div style={containerStyle}>
      <h1 style={headingStyle}>Property Manager</h1>

      {showPropertyForm ? (
        <>
          <PropertyForm
            currentProperty={editingProperty}
            onSaveSuccess={handleSaveSuccess}
          />
          <button onClick={handleCancelEdit} style={cancelEditButtonStyle}>
            Cancel
          </button>
        </>
      ) : (
        <>
          <div style={addPropertyButtonContainerStyle}>
            <button onClick={() => { setShowPropertyForm(true); setEditingProperty(null); }} style={addPropertyButtonStyle}>
              Add New Property
            </button>
          </div>
          
          <div style={searchContainerStyle}>
            <FaSearch style={searchIconStyle} />
            <input
              type="text"
              placeholder="Search by ID, Title, Project, Location, Owner..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={searchInputStyle}
            />
          </div>

          {loading && <p style={messageStyle}>Loading properties...</p>}
          {error && <p style={errorStyle}>{error}</p>}

          {!loading && properties.length > 0 && (
            <div style={tableContainerStyle}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Display ID</th>
                    <th style={thStyle}>Title</th>
                    <th style={thStyle}>Project Name</th>
                    <th style={thStyle}>Location</th>
                    <th style={thStyle}>Price (Sale)</th>
                    <th style={thStyle}>Price (Rent)</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Featured</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProperties.map((property) => (
                    <tr key={property.id} style={trStyle}>
                      <td style={tdStyle}>{property.display_id}</td>
                      <td style={tdStyle}>{property.title}</td>
                      <td style={tdStyle}>{property.project_name || '-'}</td>
                      <td style={tdStyle}>{property.location}</td>
                      <td style={tdStyle}>
                        {property.price_sale ? `RM ${property.price_sale.toLocaleString()}` : '-'}
                      </td>
                      <td style={tdStyle}>
                        {property.price_rent ? `RM ${property.price_rent.toLocaleString()}` : '-'}
                      </td>
                      <td style={tdStyle}>{property.status}</td>
                      <td style={tdStyle}>{property.is_featured ? 'Yes' : 'No'}</td>
                      <td style={tdStyle}>
                        <button onClick={() => handleEdit(property)} style={editButtonStyle}>Edit</button>
                        <button onClick={() => handleDelete(property.id, property.images)} style={deleteButtonStyle}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && properties.length === 0 && <p style={messageStyle}>No properties found. Add a new one!</p>}
        </>
      )}
    </div>
  );
}

// All your existing styles remain the same...
const containerStyle = {
  maxWidth: '1200px',
  margin: '20px auto',
  padding: '20px',
  border: '1px solid #ddd',
  borderRadius: '10px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  fontFamily: 'Arial, sans-serif',
  backgroundColor: '#fff',
};

const headingStyle = {
  textAlign: 'center',
  color: '#333',
  marginBottom: '30px',
  fontSize: '32px',
};

const messageStyle = {
  textAlign: 'center',
  color: '#555',
  fontSize: '16px',
  marginTop: '20px',
};

const errorStyle = {
  color: 'red',
  fontWeight: 'bold',
  textAlign: 'center',
  marginBottom: '15px',
};

const addPropertyButtonStyle = {
  padding: '12px 25px',
  backgroundColor: '#007bff',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '18px',
  fontWeight: 'bold',
  transition: 'background-color 0.3s ease',
};

const tableContainerStyle = {
  overflowX: 'auto',
  marginTop: '30px',
};

const searchContainerStyle = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  width: '300px',
};

const searchInputStyle = {
  width: '100%',
  padding: '10px 10px 10px 40px',
  borderRadius: '5px',
  border: '1px solid #ddd',
  fontSize: '1em',
};

const searchIconStyle = {
  position: 'absolute',
  left: '15px',
  color: '#888',
  fontSize: '1.2em',
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  marginTop: '20px',
};

const thStyle = {
  backgroundColor: '#f2f2f2',
  border: '1px solid #ddd',
  padding: '12px',
  textAlign: 'left',
  fontWeight: 'bold',
  color: '#333',
};

const tdStyle = {
  border: '1px solid #ddd',
  padding: '10px',
  textAlign: 'left',
  verticalAlign: 'top',
};

const trStyle = {
  transition: 'background-color 0.2s ease',
};

const editButtonStyle = {
  padding: '8px 15px',
  backgroundColor: '#007bff',
  color: 'white',
  border: 'none',
  borderRadius: '5px',
  cursor: 'pointer',
  fontSize: '14px',
  marginRight: '5px',
  transition: 'background-color 0.3s ease',
};

const deleteButtonStyle = {
  padding: '8px 15px',
  backgroundColor: '#dc3545',
  color: 'white',
  border: 'none',
  borderRadius: '5px',
  cursor: 'pointer',
  fontSize: '14px',
  transition: 'background-color 0.3s ease',
};

const cancelEditButtonStyle = {
  padding: '10px 20px',
  backgroundColor: '#6c757d',
  color: 'white',
  border: 'none',
  borderRadius: '5px',
  cursor: 'pointer',
  fontSize: '16px',
  marginTop: '20px',
  display: 'block',
  margin: '20px auto 0 auto',
};

const addPropertyButtonContainerStyle = {
  textAlign: 'center',
  marginBottom: '20px',
  marginTop: '10px',
};

export default PropertyManager;